
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '../../home/components/Navbar';
import Footer from '../../home/components/Footer';

const articles = [
  {
    id: 1,
    category: 'Leadership',
    title: 'Mon parcours de Tech Lead : Diriger une équipe de développeurs',
    excerpt: "Comment j'ai appris à gérer une équipe technique, les défis rencontrés et les leçons apprises en tant que Tech Lead dans différentes entreprises.",
    date: '15 Jan 2025',
    readTime: '8 min',
    image: 'https://readdy.ai/api/search-image?query=modern%20tech%20team%20meeting%20in%20office%20with%20diverse%20developers%20collaborating%20on%20project%20whiteboard%20with%20diagrams%20professional%20corporate%20environment%20warm%20lighting&width=1200&height=500&seq=blog1detail&orientation=landscape',
    tags: ['Leadership', 'Management', 'Tech Lead', 'Équipe', 'Développement'],
    featured: true,
    content: `
      <h2>Introduction</h2>
      <p>Devenir Tech Lead n'est pas simplement une promotion, c'est une transformation complète de votre rôle et de vos responsabilités. Après plusieurs années en tant que développeur, j'ai eu l'opportunité de diriger des équipes techniques dans différentes entreprises, notamment chez Value Market SARL et d'autres organisations.</p>
      
      <h2>Les défis du leadership technique</h2>
      <p>Le passage de développeur à Tech Lead implique de nombreux défis. Le premier et le plus important est de comprendre que votre succès ne se mesure plus uniquement par la qualité de votre code, mais par la performance de votre équipe.</p>
      
      <h3>1. Équilibrer technique et management</h3>
      <p>L'un des plus grands défis est de trouver le bon équilibre entre rester techniquement impliqué et consacrer du temps au management. J'ai appris qu'il est essentiel de déléguer certaines tâches techniques pour pouvoir se concentrer sur la coordination et le mentorat.</p>
      
      <h3>2. Communication avec les parties prenantes</h3>
      <p>En tant que Tech Lead, vous devenez le pont entre l'équipe technique et les autres départements. Cela nécessite de développer des compétences en communication pour traduire des concepts techniques complexes en termes compréhensibles pour tous.</p>
      
      <h3>3. Gestion des conflits et motivation</h3>
      <p>Chaque équipe a ses dynamiques propres. J'ai appris à identifier les sources de tension et à les résoudre de manière constructive, tout en maintenant la motivation et l'engagement de chaque membre.</p>
      
      <h2>Les leçons apprises</h2>
      <p>Au fil de mon parcours, j'ai tiré plusieurs leçons importantes :</p>
      <ul>
        <li><strong>L'écoute active</strong> : Prendre le temps d'écouter les préoccupations et les idées de chaque membre de l'équipe</li>
        <li><strong>La confiance</strong> : Faire confiance à son équipe et leur donner l'autonomie nécessaire pour exceller</li>
        <li><strong>L'apprentissage continu</strong> : Rester à jour techniquement tout en développant ses compétences en leadership</li>
        <li><strong>La transparence</strong> : Communiquer ouvertement sur les objectifs, les défis et les décisions</li>
      </ul>
      
      <h2>Outils et méthodologies</h2>
      <p>Pour gérer efficacement une équipe technique, j'ai mis en place plusieurs pratiques :</p>
      <ul>
        <li>Daily standups pour maintenir la communication</li>
        <li>Code reviews systématiques pour assurer la qualité</li>
        <li>Sessions de pair programming pour le transfert de connaissances</li>
        <li>Rétrospectives régulières pour l'amélioration continue</li>
      </ul>
      
      <h2>Conclusion</h2>
      <p>Le rôle de Tech Lead est exigeant mais extrêmement gratifiant. Voir son équipe grandir, réussir des projets complexes et développer leurs compétences est une source de satisfaction immense. Si vous envisagez cette transition, préparez-vous à un voyage d'apprentissage continu.</p>
    `,
  },
  {
    id: 2,
    category: 'Architecture',
    title: 'Clean Architecture avec Node.js et TypeScript',
    excerpt: 'Guide complet pour implémenter une architecture propre et maintenable dans vos projets backend avec les principes SOLID.',
    date: '10 Jan 2025',
    readTime: '12 min',
    image: 'https://readdy.ai/api/search-image?query=abstract%20software%20architecture%20diagram%20with%20clean%20geometric%20shapes%20nodes%20and%20connections%20modern%20minimalist%20design%20dark%20background%20with%20blue%20accents&width=1200&height=500&seq=blog2detail&orientation=landscape',
    tags: ['Architecture', 'Node.js', 'TypeScript', 'SOLID', 'Backend', 'Clean Code'],
    featured: false,
    content: `
      <h2>Introduction à la Clean Architecture</h2>
      <p>La Clean Architecture, popularisée par Robert C. Martin (Uncle Bob), est une approche de conception logicielle qui vise à créer des systèmes maintenables, testables et indépendants des frameworks.</p>
      
      <h2>Les principes fondamentaux</h2>
      <p>La Clean Architecture repose sur plusieurs principes clés :</p>
      
      <h3>1. Indépendance des frameworks</h3>
      <p>L'architecture ne doit pas dépendre de l'existence d'une bibliothèque particulière. Les frameworks doivent être utilisés comme des outils, pas comme des contraintes.</p>
      
      <h3>2. Testabilité</h3>
      <p>Les règles métier peuvent être testées sans l'interface utilisateur, la base de données, le serveur web ou tout autre élément externe.</p>
      
      <h3>3. Indépendance de l'UI</h3>
      <p>L'interface utilisateur peut changer facilement sans modifier le reste du système.</p>
      
      <h2>Structure du projet</h2>
      <p>Voici comment j'organise mes projets Node.js avec TypeScript :</p>
      <pre><code>
src/
├── domain/
│   ├── entities/
│   └── repositories/
├── application/
│   ├── use-cases/
│   └── services/
├── infrastructure/
│   ├── database/
│   ├── http/
│   └── external-services/
└── presentation/
    ├── controllers/
    └── routes/
      </code></pre>
      
      <h2>Implémentation avec TypeScript</h2>
      <p>TypeScript apporte une couche de typage statique qui renforce la robustesse de l'architecture. Les interfaces définissent clairement les contrats entre les différentes couches.</p>
      
      <h2>Les avantages constatés</h2>
      <ul>
        <li>Code plus facile à maintenir et à faire évoluer</li>
        <li>Tests unitaires simplifiés grâce à l'injection de dépendances</li>
        <li>Onboarding des nouveaux développeurs facilité</li>
        <li>Réduction de la dette technique</li>
      </ul>
      
      <h2>Conclusion</h2>
      <p>La Clean Architecture demande un investissement initial plus important, mais les bénéfices à long terme sont considérables. Je l'applique systématiquement dans tous mes projets backend.</p>
    `,
  },
  {
    id: 3,
    category: 'DevOps',
    title: "CI/CD avec GitLab et Kubernetes : Retour d'expérience",
    excerpt: "Comment j'ai mis en place des pipelines CI/CD robustes pour automatiser les déploiements et améliorer la productivité de l'équipe.",
    date: '5 Jan 2025',
    readTime: '10 min',
    image: 'https://readdy.ai/api/search-image?query=devops%20pipeline%20visualization%20with%20containers%20kubernetes%20pods%20deployment%20workflow%20modern%20tech%20infrastructure%20dark%20theme%20with%20orange%20and%20blue%20highlights&width=1200&height=500&seq=blog3detail&orientation=landscape',
    tags: ['DevOps', 'CI/CD', 'Kubernetes', 'GitLab', 'Docker', 'Automatisation'],
    featured: false,
    content: `
      <h2>Pourquoi le CI/CD est essentiel</h2>
      <p>L'intégration continue et le déploiement continu sont devenus des pratiques incontournables dans le développement moderne. Ils permettent de livrer des fonctionnalités plus rapidement tout en maintenant une qualité élevée.</p>
      
      <h2>Notre stack DevOps</h2>
      <p>Pour les projets chez Value Market SARL et d'autres entreprises, j'ai mis en place une stack complète :</p>
      <ul>
        <li><strong>GitLab CI/CD</strong> : Orchestration des pipelines</li>
        <li><strong>Docker</strong> : Conteneurisation des applications</li>
        <li><strong>Kubernetes</strong> : Orchestration des conteneurs</li>
        <li><strong>Nginx</strong> : Reverse proxy et load balancing</li>
      </ul>
      
      <h2>Structure d'un pipeline type</h2>
      <p>Nos pipelines suivent généralement ces étapes :</p>
      <ol>
        <li><strong>Build</strong> : Compilation et création de l'image Docker</li>
        <li><strong>Test</strong> : Exécution des tests unitaires et d'intégration</li>
        <li><strong>Security Scan</strong> : Analyse de vulnérabilités</li>
        <li><strong>Deploy Staging</strong> : Déploiement en environnement de test</li>
        <li><strong>Deploy Production</strong> : Déploiement en production (manuel ou automatique)</li>
      </ol>
      
      <h2>Kubernetes en production</h2>
      <p>L'utilisation de Kubernetes nous a permis de :</p>
      <ul>
        <li>Gérer facilement le scaling des applications</li>
        <li>Assurer une haute disponibilité</li>
        <li>Simplifier les rollbacks en cas de problème</li>
        <li>Standardiser les déploiements entre environnements</li>
      </ul>
      
      <h2>Les défis rencontrés</h2>
      <p>La mise en place n'a pas été sans obstacles. Les principaux défis incluaient la gestion des secrets, la configuration des environnements et la formation de l'équipe aux nouvelles pratiques.</p>
      
      <h2>Résultats obtenus</h2>
      <p>Après l'implémentation complète, nous avons constaté :</p>
      <ul>
        <li>Réduction de 70% du temps de déploiement</li>
        <li>Diminution significative des erreurs en production</li>
        <li>Amélioration de la confiance de l'équipe dans les releases</li>
      </ul>
      
      <h2>Conclusion</h2>
      <p>Le CI/CD avec GitLab et Kubernetes est un investissement qui porte ses fruits rapidement. Je recommande fortement cette approche pour toute équipe souhaitant améliorer sa productivité.</p>
    `,
  },
  {
    id: 4,
    category: 'Management',
    title: 'Chef de Département IT : Gérer les projets et les équipes',
    excerpt: "Les responsabilités d'un chef de département IT, la gestion des budgets, des ressources humaines et la coordination des projets stratégiques.",
    date: '28 Déc 2024',
    readTime: '15 min',
    image: 'https://readdy.ai/api/search-image?query=executive%20IT%20manager%20in%20modern%20office%20reviewing%20project%20dashboard%20on%20large%20screen%20strategic%20planning%20meeting%20professional%20corporate%20setting&width=1200&height=500&seq=blog4detail&orientation=landscape',
    tags: ['Management', 'IT', 'Stratégie', 'Leadership', 'Gestion de projet', 'RH'],
    featured: true,
    content: `
      <h2>Le rôle de Chef de Département IT</h2>
      <p>Être Chef de Département IT va bien au-delà de la simple gestion technique. C'est un rôle qui combine leadership, stratégie, gestion financière et développement des talents.</p>
      
      <h2>Mes responsabilités principales</h2>
      
      <h3>1. Vision stratégique</h3>
      <p>Définir la roadmap technologique de l'entreprise en alignement avec les objectifs business. Cela implique d'anticiper les tendances, d'évaluer les nouvelles technologies et de planifier les évolutions.</p>
      
      <h3>2. Gestion des équipes</h3>
      <p>Manager plusieurs équipes de développeurs, avec des profils variés (frontend, backend, mobile, DevOps). Chaque équipe a ses dynamiques propres et nécessite une approche adaptée.</p>
      
      <h3>3. Gestion budgétaire</h3>
      <p>Élaborer et gérer le budget du département, justifier les investissements technologiques et optimiser les coûts tout en maintenant la qualité.</p>
      
      <h3>4. Relations inter-départements</h3>
      <p>Collaborer avec les autres départements (Marketing, Finance, RH) pour comprendre leurs besoins et proposer des solutions technologiques adaptées.</p>
      
      <h2>Les défis du management IT</h2>
      <p>Plusieurs défis se présentent quotidiennement :</p>
      <ul>
        <li>Recruter et retenir les talents dans un marché compétitif</li>
        <li>Maintenir la motivation des équipes sur des projets longs</li>
        <li>Gérer les priorités conflictuelles entre projets</li>
        <li>Assurer la montée en compétences continue des équipes</li>
      </ul>
      
      <h2>Outils de gestion</h2>
      <p>Pour gérer efficacement le département, j'utilise :</p>
      <ul>
        <li>Jira pour le suivi des projets</li>
        <li>Confluence pour la documentation</li>
        <li>Slack pour la communication</li>
        <li>Des tableaux de bord personnalisés pour le reporting</li>
      </ul>
      
      <h2>Conseils pour les futurs managers IT</h2>
      <ol>
        <li>Restez proche de vos équipes malgré les responsabilités administratives</li>
        <li>Investissez dans la formation et le développement des talents</li>
        <li>Communiquez régulièrement sur la vision et les objectifs</li>
        <li>Célébrez les succès, même les petits</li>
      </ol>
      
      <h2>Conclusion</h2>
      <p>Le rôle de Chef de Département IT est exigeant mais passionnant. Il offre l'opportunité d'avoir un impact significatif sur l'organisation tout en continuant à évoluer dans le domaine technologique.</p>
    `,
  },
  {
    id: 5,
    category: 'Backend',
    title: 'Optimisation des API REST avec Redis et Caching',
    excerpt: 'Techniques avancées pour améliorer les performances de vos API avec la mise en cache Redis et les bonnes pratiques de conception.',
    date: '20 Déc 2024',
    readTime: '9 min',
    image: 'https://readdy.ai/api/search-image?query=database%20optimization%20visualization%20with%20redis%20cache%20nodes%20data%20flow%20diagram%20modern%20tech%20aesthetic%20dark%20background%20with%20red%20accents&width=1200&height=500&seq=blog5detail&orientation=landscape',
    tags: ['Backend', 'Redis', 'API', 'Performance', 'Cache', 'Optimisation'],
    featured: false,
    content: `
      <h2>L'importance du caching</h2>
      <p>Dans le développement d'API à haute performance, le caching est une technique essentielle. Redis, en tant que store de données en mémoire, offre des performances exceptionnelles pour ce cas d'usage.</p>
      
      <h2>Quand utiliser le cache ?</h2>
      <p>Le caching est particulièrement utile pour :</p>
      <ul>
        <li>Les données fréquemment accédées mais rarement modifiées</li>
        <li>Les résultats de calculs coûteux</li>
        <li>Les réponses d'API externes</li>
        <li>Les sessions utilisateur</li>
      </ul>
      
      <h2>Stratégies de caching</h2>
      
      <h3>1. Cache-Aside (Lazy Loading)</h3>
      <p>L'application vérifie d'abord le cache. Si les données ne sont pas présentes, elle les récupère de la base de données et les stocke dans le cache.</p>
      
      <h3>2. Write-Through</h3>
      <p>Les données sont écrites simultanément dans le cache et la base de données, garantissant la cohérence.</p>
      
      <h3>3. Write-Behind</h3>
      <p>Les données sont d'abord écrites dans le cache, puis de manière asynchrone dans la base de données.</p>
      
      <h2>Implémentation avec Redis</h2>
      <p>Voici les bonnes pratiques que j'applique :</p>
      <ul>
        <li>Définir des TTL (Time To Live) appropriés pour chaque type de données</li>
        <li>Utiliser des clés de cache structurées et prévisibles</li>
        <li>Implémenter des mécanismes d'invalidation du cache</li>
        <li>Monitorer les hit/miss ratios</li>
      </ul>
      
      <h2>Résultats obtenus</h2>
      <p>Sur le projet FOTOL_JAY, l'implémentation du caching Redis a permis :</p>
      <ul>
        <li>Réduction de 80% du temps de réponse moyen</li>
        <li>Diminution de 60% de la charge sur la base de données</li>
        <li>Amélioration significative de l'expérience utilisateur</li>
      </ul>
      
      <h2>Conclusion</h2>
      <p>Redis est un outil puissant pour optimiser les performances de vos API. Une stratégie de caching bien pensée peut transformer radicalement les performances de votre application.</p>
    `,
  },
  {
    id: 6,
    category: 'Mobile',
    title: 'Flutter : Développer des applications cross-platform performantes',
    excerpt: 'Mon expérience avec Flutter pour créer des applications mobiles natives pour iOS et Android avec une seule base de code.',
    date: '15 Déc 2024',
    readTime: '11 min',
    image: 'https://readdy.ai/api/search-image?query=mobile%20app%20development%20flutter%20framework%20smartphone%20mockups%20with%20beautiful%20UI%20design%20modern%20minimalist%20style%20gradient%20background&width=1200&height=500&seq=blog6detail&orientation=landscape',
    tags: ['Mobile', 'Flutter', 'Dart', 'Cross-platform', 'iOS', 'Android'],
    featured: false,
    content: `
      <h2>Pourquoi Flutter ?</h2>
      <p>Après avoir exploré plusieurs frameworks de développement mobile, j'ai choisi Flutter pour sa performance native, son système de widgets riche et sa productivité exceptionnelle.</p>
      
      <h2>Mes projets Flutter</h2>
      <p>J'ai développé plusieurs applications avec Flutter :</p>
      <ul>
        <li><strong>Messaging App</strong> : Clone WhatsApp avec chat temps réel</li>
        <li><strong>Task Management App</strong> : Gestion de tâches collaboratives</li>
        <li><strong>Event Management App</strong> : Billetterie et gestion d'événements</li>
        <li><strong>Marketplace Mobile App</strong> : Plateforme de vente de produits</li>
      </ul>
      
      <h2>Les avantages de Flutter</h2>
      
      <h3>1. Hot Reload</h3>
      <p>Le Hot Reload permet de voir instantanément les modifications du code, accélérant considérablement le développement.</p>
      
      <h3>2. Widgets personnalisables</h3>
      <p>Flutter offre une bibliothèque riche de widgets Material et Cupertino, tout en permettant une personnalisation complète.</p>
      
      <h3>3. Performance native</h3>
      <p>Contrairement aux solutions hybrides, Flutter compile en code natif, offrant des performances comparables aux applications natives.</p>
      
      <h2>Architecture recommandée</h2>
      <p>Pour mes projets Flutter, j'utilise généralement :</p>
      <ul>
        <li><strong>Provider ou Riverpod</strong> pour la gestion d'état</li>
        <li><strong>Clean Architecture</strong> pour la structure du projet</li>
        <li><strong>Dio</strong> pour les appels HTTP</li>
        <li><strong>Hive ou SQLite</strong> pour le stockage local</li>
      </ul>
      
      <h2>Intégration avec Firebase</h2>
      <p>Firebase s'intègre parfaitement avec Flutter pour :</p>
      <ul>
        <li>L'authentification</li>
        <li>Le stockage de fichiers</li>
        <li>Les notifications push</li>
        <li>La base de données temps réel</li>
      </ul>
      
      <h2>Conseils pour débuter</h2>
      <ol>
        <li>Maîtrisez d'abord les bases de Dart</li>
        <li>Comprenez le système de widgets et leur cycle de vie</li>
        <li>Pratiquez avec des projets simples avant de passer à des applications complexes</li>
        <li>Explorez les packages disponibles sur pub.dev</li>
      </ol>
      
      <h2>Conclusion</h2>
      <p>Flutter est devenu mon framework de prédilection pour le développement mobile. Sa courbe d'apprentissage est raisonnable et les résultats sont impressionnants.</p>
    `,
  },
];

export default function BlogDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const article = articles.find((a) => a.id === Number(id));
  const relatedArticles = articles.filter(
    (a) => a.id !== Number(id) && a.category === article?.category
  );
  const otherArticles =
    relatedArticles.length < 2
      ? [
          ...relatedArticles,
          ...articles
            .filter(
              (a) => a.id !== Number(id) && a.category !== article?.category
            )
            .slice(0, 2 - relatedArticles.length),
        ]
      : relatedArticles.slice(0, 2);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  if (!article) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-[#0A0A0A] mb-4">
            Article non trouvé
          </h1>
          <p className="text-gray-600 mb-8">
            L'article que vous recherchez n'existe pas.
          </p>
          <button
            onClick={() => navigate('/blog')}
            className="inline-flex items-center gap-2 bg-[#0066FF] text-white px-6 py-3 rounded-full font-semibold hover:bg-[#0052CC] transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            Retour au blog
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Image */}
      <section className="relative h-[500px] overflow-hidden">
        <img
          src={article.image}
          alt={article.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-black/30"></div>

        {/* Back Button */}
        <button
          onClick={() => navigate('/blog')}
          className="absolute top-24 left-6 md:left-12 z-10 inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-white/20 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-arrow-left-line"></i>
          Retour au blog
        </button>

        {/* Article Header */}
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-4">
              <span className="bg-[#0066FF] text-white px-4 py-1.5 rounded-full text-sm font-medium">
                {article.category}
              </span>
              {article.featured && (
                <span className="bg-yellow-500 text-black px-4 py-1.5 rounded-full text-sm font-medium">
                  Featured
                </span>
              )}
            </div>
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 leading-tight">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-6 text-gray-300">
              <span className="flex items-center gap-2">
                <i className="ri-calendar-line"></i>
                {article.date}
              </span>
              <span className="flex items-center gap-2">
                <i className="ri-time-line"></i>
                {article.readTime} de lecture
              </span>
              <span className="flex items-center gap-2">
                <i className="ri-user-line"></i>
                Chérif Ibrahima Aidara Sadio
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Excerpt */}
          <div className="bg-gray-50 rounded-2xl p-6 mb-12 border-l-4 border-[#0066FF]">
            <p className="text-lg text-gray-700 italic">{article.excerpt}</p>
          </div>

          {/* Content */}
          <article
            className="prose prose-lg max-w-none prose-headings:text-[#0A0A0A] prose-headings:font-bold prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4 prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3 prose-p:text-gray-600 prose-p:leading-relaxed prose-p:mb-4 prose-ul:my-4 prose-li:text-gray-600 prose-li:mb-2 prose-strong:text-[#0A0A0A] prose-a:text-[#0066FF] prose-a:no-underline hover:prose-a:underline prose-pre:bg-[#0A0A0A] prose-pre:text-gray-300 prose-pre:rounded-xl prose-pre:p-4 prose-code:text-[#0066FF] prose-code:bg-gray-100 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-ol:my-4"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {/* Tags */}
          <div className="mt-12 pt-8 border-t border-gray-200">
            <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
              Tags
            </h4>
            <div className="flex flex-wrap gap-2">
              {article.tags.map((tag, index) => (
                <span
                  key={index}
                  className="bg-gray-100 text-gray-700 px-4 py-2 rounded-full text-sm font-medium hover:bg-[#0066FF] hover:text-white transition-colors cursor-pointer"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {/* Author Card */}
          <div className="mt-12 bg-gradient-to-r from-[#0A0A0A] to-[#1a1a1a] rounded-3xl p-8 flex flex-col md:flex-row items-center gap-6">
            <img
              src="https://static.readdy.ai/image/c21dcdc26ecafc1fbc46806db6089fae/7d4ef6e928f286311fb143e3834faf20.jpeg"
              alt="Chérif Ibrahima Aidara Sadio"
              className="w-24 h-24 rounded-full object-cover border-4 border-[#0066FF]"
            />
            <div className="text-center md:text-left">
              <h4 className="text-xl font-bold text-white mb-2">
                Chérif Ibrahima Aidara Sadio
              </h4>
              <p className="text-[#0066FF] font-medium mb-2">
                Ingénieur Logiciel | Concepteur UX/UI
              </p>
              <p className="text-gray-400 text-sm">
                3 ans d'expérience en développement web et mobile.
                Passionné par la création d'expériences utilisateur et le partage de
                connaissances.
              </p>
            </div>
          </div>

          {/* Share */}
          <div className="mt-8 flex items-center justify-center gap-4">
            <span className="text-gray-500 text-sm">
              Partager cet article :
            </span>
            <div className="flex items-center gap-3">
              <a
                href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(
                  article.title
                )}&url=${encodeURIComponent(window.location.href)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center hover:bg-[#1DA1F2] hover:text-white transition-colors cursor-pointer"
              >
                <i className="ri-twitter-x-line"></i>
              </a>
              <a
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
                  window.location.href
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center hover:bg-[#0077B5] hover:text-white transition-colors cursor-pointer"
              >
                <i className="ri-linkedin-fill"></i>
              </a>
              <button
                onClick={() => navigator.clipboard.writeText(window.location.href)}
                className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center hover:bg-[#0066FF] hover:text-white transition-colors cursor-pointer"
              >
                <i className="ri-link"></i>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Related Articles */}
      <section className="py-16 px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <h3 className="text-2xl font-bold text-[#0A0A0A] mb-8 text-center">
            Articles similaires
          </h3>
          <div className="grid md:grid-cols-2 gap-8">
            {otherArticles.map((relatedArticle) => (
              <Link
                key={relatedArticle.id}
                to={`/blog/${relatedArticle.id}`}
                className="group bg-white rounded-2xl overflow-hidden border border-gray-100 hover:shadow-xl hover:-translate-y-2 transition-all duration-300 cursor-pointer"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={relatedArticle.image.replace(
                      'width=1200&height=500',
                      'width=600&height=300'
                    )}
                    alt={relatedArticle.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = 'https://readdy.ai/api/search-image?query=minimalist%20technology%20abstract%20background%20with%20geometric%20patterns%20blue%20accents%20modern%20digital%20workspace%20clean%20professional%20tech%20aesthetic&width=600&height=300&seq=fallback&orientation=landscape';
                    }}
                  />
                  <div className="absolute top-4 left-4">
                    <span className="bg-[#0066FF] text-white px-3 py-1 rounded-full text-xs font-medium">
                      {relatedArticle.category}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <h4 className="text-lg font-bold text-[#0A0A0A] mb-3 group-hover:text-[#0066FF] transition-colors line-clamp-2">
                    {relatedArticle.title}
                  </h4>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                    {relatedArticle.excerpt}
                  </p>
                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <div className="flex items-center gap-3 text-gray-500 text-xs">
                      <span className="flex items-center gap-1">
                        <i className="ri-calendar-line"></i>
                        {relatedArticle.date}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-time-line"></i>
                        {relatedArticle.readTime}
                      </span>
                    </div>
                    <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center group-hover:bg-[#0066FF] transition-colors">
                      <i className="ri-arrow-right-line text-sm group-hover:text-white text-gray-600"></i>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* Back to Blog */}
          <div className="text-center mt-12">
            <button
              onClick={() => navigate('/blog')}
              className="inline-flex items-center gap-2 bg-[#0066FF] text-white px-8 py-4 rounded-full font-semibold hover:bg-[#0052CC] transition-all duration-300 cursor-pointer whitespace-nowrap shadow-lg hover:shadow-xl hover:-translate-y-1"
            >
              <i className="ri-article-line"></i>
              Voir tous les articles
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
